import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import axios from 'axios'
import { Calendar, Check } from 'lucide-react'
import moment from 'moment'
import { Modal } from 'rsuite'
import styled from 'styled-components'

import { Spinner } from 'core/components'
import routes from 'core/routes'
import { api } from 'core/services'
import { orulTokenManager } from 'core/services/orul-token-manager'

import { TRootReducer } from 'store'

import { TSweepstake, TLuckyNumber, TPrize } from '../types'
import {
  EmptyState,
  ModalContent,
  ModalDivider,
  ModalHeaderStack,
  ModalSection,
  ModalSubtitle,
  ModalTitle,
  PrizeAvailableAt,
  PrizeCard,
  PrizeDescription,
  PrizeImage,
  PrizeImageContainer,
  PrizeInfo,
  PrizeName,
  PrizeNumber,
  PrizesContainer,
  SectionLabel,
  SweepstakeCardLuckyNumber,
  SweepstakeCardLuckyNumberContainer,
  TicketsGrid,
} from './style'

const WonPrizeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #059669;
  letter-spacing: 0.02em;
`

export type SweepstakeModalProps = {
  selectedSweepstake: TSweepstake | null
  setSelectedSweepstake: (sweepstake: TSweepstake | null) => void
  setNumberModalContext: (
    context: { sweepstake: TSweepstake; luckyNumber: TLuckyNumber } | null,
  ) => void
  setWinnerNumberPrize: (prize: TPrize | null) => void
}

export function SweepstakeModal({
  selectedSweepstake,
  setSelectedSweepstake,
  setNumberModalContext,
  setWinnerNumberPrize,
}: SweepstakeModalProps) {
  const phoneNumber = useSelector((state: any) =>
    state.user.attributes.phone_number.replace('+', ''),
  )
  const { currentOrg } = useSelector((state: TRootReducer) => state.app)
  const [fetchingNumbers, setFetchingNumbers] = useState(false)
  const [luckyNumbers, setLuckyNumbers] = useState<TLuckyNumber[]>([])
  const [prizes, setPrizes] = useState<TPrize[]>([])
  const [expandedNumber, setExpandedNumber] = useState<TLuckyNumber | null>(
    null,
  )
  const [selectedPrizeId, setSelectedPrizeId] = useState<number | null>(null)
  useEffect(() => {
    if (!selectedSweepstake) return

    const fetchLuckyNumbers = async () => {
      setFetchingNumbers(true)
      const response = await api.get<TSweepstake>(
        `${routes.api.sweepstakes.info(
          phoneNumber,
          String(selectedSweepstake.sweepstake_id),
        )}`,
        {
          headers: {
            'X-App-Token': currentOrg?.token || '',
            authorization: `Bearer ${orulTokenManager.getToken()}`,
          },
        },
      )
      setFetchingNumbers(false)
      setLuckyNumbers(response.data.luckyNumbers || [])
      setPrizes(response.data.prizes || [])
    }
    fetchLuckyNumbers()
  }, [selectedSweepstake, phoneNumber])
  return (
    <Modal
      open={!!selectedSweepstake}
      onClose={() => {
        setSelectedSweepstake(null)
        setExpandedNumber(null)
        setSelectedPrizeId(null)
      }}
      size="md"
    >
      <Modal.Header>
        <Modal.Title>Detalhes do sorteio</Modal.Title>
      </Modal.Header>
      {selectedSweepstake ? (
        <Modal.Body>
          <ModalContent>
            <ModalHeaderStack>
              <ModalTitle>{selectedSweepstake.name}</ModalTitle>
              <ModalSubtitle>{selectedSweepstake.description}</ModalSubtitle>
            </ModalHeaderStack>
            {/* <InfoGrid>
              <InfoItem>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>{statusLabels[selectedSweepstake.status]}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Período</InfoLabel>
                <InfoValue>N/A - N/A</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Total de números</InfoLabel>
                <InfoValue>{luckyNumbers.length}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Código</InfoLabel>
                <InfoValue>{selectedSweepstake.sweepstake_id}</InfoValue>
              </InfoItem>
            </InfoGrid> */}
            <ModalSection>
              <>
                <SectionLabel>Prêmios do sorteio</SectionLabel>

                <PrizesContainer>
                  {prizes?.map((prize, index) => (
                    <PrizeCard
                      key={prize.id}
                      $active={selectedPrizeId === prize.id}
                      $won={
                        !!luckyNumbers.find(
                          n => n.prizeid === prize.id && n.withwin,
                        )
                      }
                      onClick={() =>
                        setSelectedPrizeId(
                          selectedPrizeId === prize.id ? null : prize.id,
                        )
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSelectedPrizeId(
                            selectedPrizeId === prize.id ? null : prize.id,
                          )
                        }
                      }}
                    >
                      <PrizeInfo>
                        <PrizeNumber $active={selectedPrizeId === prize.id}>
                          {index + 1}º
                        </PrizeNumber>
                        <span
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                          }}
                        >
                          <PrizeName>{prize.name}</PrizeName>

                          <PrizeDescription>
                            {prize.description}
                          </PrizeDescription>

                          <PrizeAvailableAt>
                            <Calendar size={12} />
                            {moment(prize.resultAvailableAt).format(
                              'DD/MM/YYYY',
                            )}
                          </PrizeAvailableAt>
                        </span>
                      </PrizeInfo>
                      {prize.imageUrl && (
                        <PrizeImageContainer>
                          <PrizeImage
                            src={`${process.env.REACT_APP_CDN}/${prize.imageUrl}`}
                            alt={prize.name}
                          />
                        </PrizeImageContainer>
                      )}
                      {luckyNumbers.find(
                        luckyNumber => luckyNumber.prizeid === prize.id,
                      ) && (
                        <WonPrizeBadge key={`won-${prize.id}`}>
                          <Check size={12} strokeWidth={2.5} />
                          <span>Você ganhou</span>
                        </WonPrizeBadge>
                      )}
                    </PrizeCard>
                  ))}
                </PrizesContainer>
              </>
            </ModalSection>
            <ModalDivider />
            <ModalSection>
              {fetchingNumbers ? (
                <Spinner />
              ) : (
                <>
                  <SectionLabel>Seus números da sorte</SectionLabel>
                  {luckyNumbers.length > 0 ? (
                    <SweepstakeCardLuckyNumberContainer>
                      <TicketsGrid>
                        {luckyNumbers.map(luckyNumber => {
                          const isFinished =
                            selectedSweepstake.status === 'finished'
                          const isWinner = luckyNumber.withwin
                          const setVariant = () => {
                            if (isWinner) return 'winner'
                            if (isFinished) return 'loser'
                            return 'default'
                          }
                          const variant = setVariant()
                          return (
                            <SweepstakeCardLuckyNumber
                              key={`number-${luckyNumber.id}`}
                              $active={expandedNumber?.id === luckyNumber.id}
                              $variant={variant}
                              onClick={() => {
                                setExpandedNumber(luckyNumber)
                                setWinnerNumberPrize(
                                  prizes.find(
                                    prize => prize.id === luckyNumber.prizeid,
                                  ) || null,
                                )
                                setNumberModalContext({
                                  sweepstake: selectedSweepstake,
                                  luckyNumber,
                                })
                              }}
                            >
                              {luckyNumber.number}
                            </SweepstakeCardLuckyNumber>
                          )
                        })}
                      </TicketsGrid>
                    </SweepstakeCardLuckyNumberContainer>
                  ) : (
                    <EmptyState>
                      {' '}
                      Parece que você não tem nenhum número da sorte para este
                      sorteio.
                    </EmptyState>
                  )}
                </>
              )}
            </ModalSection>
          </ModalContent>
        </Modal.Body>
      ) : null}
    </Modal>
  )
}
